import React, { useState } from 'react';
import { Card, Button, Space, Table, Tag, Modal, Form, Input, Select, InputNumber, Typography, Alert, Divider, message } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SaveOutlined, BranchesOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API } from '@/services/api.service';

const { TextArea } = Input;
const { Text, Title } = Typography;

export default function PipelineBuilderTab({ config }) {
  const { t } = useTranslation(['tables']);
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [stepModalVisible, setStepModalVisible] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [pipelineType, setPipelineType] = useState(config?.orchestration?.type || 'sequential');
  const [steps, setSteps] = useState(config?.orchestration?.steps || []);

  const pa = (key) => t(`payrollAutomation.agentConfig.${key}`, { ns: 'tables', defaultValue: key });

  const isPipelineMode = config?.mode === 'pipeline';

  // Save pipeline configuration
  const savePipelineMutation = useMutation({
    mutationFn: (pipelineData) =>
      API.agentConfig.updateAgentConfiguration(config._id, pipelineData),
    onSuccess: () => {
      queryClient.invalidateQueries(['agent-config-detail', config._id]);
    },
  });

  const handleAddStep = () => {
    setEditingStep(null);
    form.resetFields();
    setStepModalVisible(true);
  };

  const handleEditStep = (step) => {
    setEditingStep(step);

    // Convert arrays/objects back to strings for form display
    const formValues = { ...step };

    if (formValues.inputMapping && typeof formValues.inputMapping === 'object') {
      formValues.inputMapping = JSON.stringify(formValues.inputMapping, null, 2);
    }

    if (formValues.mergeInputs && Array.isArray(formValues.mergeInputs)) {
      formValues.mergeInputs = formValues.mergeInputs.join(', ');
    }

    form.setFieldsValue(formValues);
    setStepModalVisible(true);
  };

  const handleDeleteStep = (stepId) => {
    Modal.confirm({
      title: pa('deleteStep'),
      content: pa('deleteStepConfirm'),
      onOk: () => {
        const newSteps = steps.filter((s) => s.stepId !== stepId);
        setSteps(newSteps);
      },
    });
  };

  const handleSaveStep = async () => {
    try {
      const values = await form.validateFields();

      // Parse JSON fields
      let parsedValues = { ...values };

      // Parse inputMapping if it's a string
      if (values.inputMapping && typeof values.inputMapping === 'string') {
        try {
          parsedValues.inputMapping = JSON.parse(values.inputMapping);
        } catch {
          message.error('Invalid JSON in Input Mapping');
          return;
        }
      }

      // Parse mergeInputs (convert comma-separated string to array)
      if (values.mergeInputs && typeof values.mergeInputs === 'string') {
        parsedValues.mergeInputs = values.mergeInputs.split(',').map(s => s.trim()).filter(Boolean);
      }

      if (editingStep) {
        // Update existing step
        const newSteps = steps.map((s) =>
          s.stepId === editingStep.stepId ? { ...s, ...parsedValues } : s
        );
        setSteps(newSteps);
      } else {
        // Add new step
        const newStepId = steps.length > 0 ? Math.max(...steps.map((s) => s.stepId)) + 1 : 1;
        const newStep = {
          ...parsedValues,
          stepId: newStepId,
        };
        setSteps([...steps, newStep]);
      }

      setStepModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleSavePipeline = () => {
    const pipelineConfig = {
      mode: 'pipeline',
      orchestration: {
        type: pipelineType,
        steps: steps,
      },
    };

    savePipelineMutation.mutate(pipelineConfig, {
      onSuccess: () => {
        message.success(pa('pipelineConfigSaved'));
      },
      onError: (error) => {
        message.error(`${pa('pipelineSaveFailed')}: ${error.response?.data?.message || error.message}`);
      },
    });
  };

  const handleEnablePipeline = () => {
    Modal.confirm({
      title: pa('enablePipelineMode'),
      content: pa('enablePipelineModeDesc'),
      onOk: () => {
        const pipelineConfig = {
          mode: 'pipeline',
          orchestration: {
            type: 'sequential',
            steps: [],
          },
        };
        savePipelineMutation.mutate(pipelineConfig);
      },
    });
  };

  const handleDisablePipeline = () => {
    Modal.confirm({
      title: pa('disablePipelineMode'),
      content: pa('disablePipelineModeConfirm'),
      okText: pa('yes'),
      cancelText: pa('cancel'),
      okButtonProps: { danger: true },
      onOk: () => {
        const singleModeConfig = {
          mode: 'single',
          orchestration: {
            type: 'sequential',
            steps: [],
          },
        };
        savePipelineMutation.mutate(singleModeConfig, {
          onSuccess: () => {
            message.success(pa('pipelineDisabled'));
          },
        });
      },
    });
  };

  const stepColumns = [
    {
      title: pa('order'),
      dataIndex: 'stepId',
      key: 'stepId',
      width: 80,
      render: (stepId) => <Tag color="blue">#{stepId}</Tag>,
    },
    {
      title: pa('stepName'),
      dataIndex: 'stepName',
      key: 'stepName',
      render: (name, record) => (
        <div>
          <Text strong>{name}</Text>
          {record.stepType === 'agent' && (
            <div>
              <Tag color="purple">{record.agentName || pa('agentName')}</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: pa('type'),
      dataIndex: 'stepType',
      key: 'stepType',
      width: 120,
      render: (type) => {
        const colors = { agent: 'green', merge: 'orange', transform: 'cyan' };
        return <Tag color={colors[type]}>{type.toUpperCase()}</Tag>;
      },
    },
    {
      title: pa('outputVariable'),
      dataIndex: 'outputVariable',
      key: 'outputVariable',
      width: 150,
      render: (varName) => varName && <code>{`{{${varName}}}`}</code>,
    },
    {
      title: pa('condition'),
      dataIndex: 'condition',
      key: 'condition',
      render: (cond) => cond && <Text type="secondary" code>{cond}</Text>,
    },
    {
      title: pa('actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditStep(record)}
          >
            {pa('edit')}
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteStep(record.stepId)}
          >
            {pa('delete')}
          </Button>
        </Space>
      ),
    },
  ];

  if (!config) {
    return (
      <Alert
        message={pa('noConfigSelected')}
        description={pa('selectOrCreateConfig')}
        type="info"
        showIcon
      />
    );
  }

  if (!isPipelineMode) {
    return (
      <Card>
        <div style={{ padding: '40px 0' }}>
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <BranchesOutlined style={{ fontSize: 64, color: '#1890ff', marginBottom: 16 }} />
            <Title level={4}>{pa('multiAgentPipeline')}</Title>
            <Text type="secondary">{pa('enablePipelineModeDesc')}</Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              className="btn-primary"
              size="large"
              icon={<BranchesOutlined />}
              onClick={handleEnablePipeline}
              loading={savePipelineMutation.isPending}
            >
              {pa('enablePipelineMode')}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Text strong>{pa('pipelineExecutionType')}</Text>
              <div style={{ marginTop: 8 }}>
                <Select
                  value={pipelineType}
                  onChange={setPipelineType}
                  style={{ width: '100%', maxWidth: 600 }}
                >
                  <Select.Option value="sequential">
                    {pa('sequential')} - {pa('sequentialDesc')}
                  </Select.Option>
                  <Select.Option value="parallel">
                    {pa('parallel')} - {pa('parallelDesc')}
                  </Select.Option>
                </Select>
              </div>
            </div>
            <Button
              danger
              onClick={handleDisablePipeline}
              loading={savePipelineMutation.isPending}
            >
              {pa('disablePipelineMode')}
            </Button>
          </div>

          <Divider />

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={5} style={{ margin: 0 }}>{pa('pipelineSteps')} ({steps.length})</Title>
              <Space>
                <Button
                  className="btn-primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddStep}
                >
                  {pa('addStep')}
                </Button>
                <Button
                  className="btn-primary"
                  icon={<SaveOutlined />}
                  onClick={handleSavePipeline}
                  loading={savePipelineMutation.isPending}
                  disabled={steps.length === 0}
                >
                  {pa('savePipeline')}
                </Button>
              </Space>
            </div>

            {steps.length === 0 ? (
              <Alert
                message={pa('noStepsConfigured')}
                description={pa('noStepsConfiguredDesc')}
                type="info"
                showIcon
              />
            ) : (
              <Table
                dataSource={steps}
                columns={stepColumns}
                rowKey="stepId"
                pagination={false}
                bordered
              />
            )}
          </div>
        </Space>
      </Card>

      {/* Step Editor Modal */}
      <Modal
        title={editingStep ? pa('editPipelineStep') : pa('addPipelineStep')}
        open={stepModalVisible}
        onCancel={() => {
          setStepModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSaveStep}
        width={800}
        okText={pa('save')}
        cancelText={pa('cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="stepName"
            label={pa('stepName')}
            rules={[{ required: true, message: pa('stepName') }]}
          >
            <Input placeholder={pa('stepNamePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="stepType"
            label={pa('stepType')}
            rules={[{ required: true, message: pa('stepType') }]}
            initialValue="agent"
          >
            <Select
              options={[
                { value: 'agent', label: pa('agentStepType') },
                { value: 'merge', label: pa('mergeStepType') },
                { value: 'transform', label: pa('transformStepType') },
              ]}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(prev, curr) => prev.stepType !== curr.stepType}>
            {() => {
              const stepType = form.getFieldValue('stepType');

              if (stepType === 'agent') {
                return (
                  <>
                    <Form.Item name="agentName" label={pa('agentName')}>
                      <Input placeholder={pa('agentNamePlaceholder')} />
                    </Form.Item>

                    <Form.Item
                      name="systemPrompt"
                      label={pa('systemPrompt')}
                      rules={[{ required: true, message: pa('systemPrompt') }]}
                    >
                      <TextArea
                        rows={6}
                        placeholder="You are an expert PCCC parser. Parse {{foreignExcel}} and extract employee data..."
                      />
                    </Form.Item>

                    <Form.Item name="inputMapping" label={pa('inputMapping')}>
                      <TextArea
                        rows={4}
                        placeholder={pa('inputMappingPlaceholder')}
                      />
                    </Form.Item>

                    <Form.Item name="modelVersion" label={pa('modelVersion')} initialValue="gemini-3.6-flash">
                      <Select
                        options={[
                          // Google Gemini models (1.5 and 2.0 were retired by Google)
                          { value: 'gemini-3.6-flash', label: 'Gemini 3.6 Flash (Recommended)' },
                          // Anthropic Claude models
                          { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (Latest)' },
                          { value: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet' },
                          { value: 'claude-3-opus-20240229', label: 'Claude 3 Opus' },
                          { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku' },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item name="maxTokens" label={pa('maxTokens')} initialValue={8192}>
                      <InputNumber min={1024} max={200000} step={1024} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="temperature" label={pa('temperature')} initialValue={0}>
                      <InputNumber min={0} max={1} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>
                  </>
                );
              }

              if (stepType === 'merge') {
                return (
                  <>
                    <Form.Item name="mergeOperation" label={pa('mergeOperation')} initialValue="concat">
                      <Select
                        options={[
                          { value: 'concat', label: pa('concatOperation') },
                          { value: 'merge', label: pa('mergeObjectsOperation') },
                          { value: 'combine', label: pa('combineOperation') },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item name="mergeInputs" label={pa('inputVariables')}>
                      <Input placeholder={pa('inputVariablesPlaceholder')} />
                    </Form.Item>
                  </>
                );
              }

              if (stepType === 'transform') {
                return (
                  <>
                    <Form.Item name="transformInput" label={pa('transformInput')} rules={[{ required: true }]}>
                      <Input placeholder={pa('transformInputPlaceholder')} />
                    </Form.Item>

                    <Form.Item name="transformOperation" label={pa('transformOperation')} initialValue="filter">
                      <Select
                        options={[
                          { value: 'filter', label: pa('filterOperation') },
                          { value: 'map', label: pa('mapOperation') },
                          { value: 'pluck', label: pa('pluckOperation') },
                          { value: 'flatten', label: pa('flattenOperation') },
                          { value: 'unique', label: pa('uniqueOperation') },
                          { value: 'sort', label: pa('sortOperation') },
                          { value: 'count', label: pa('countOperation') },
                          { value: 'custom', label: pa('customOperation') },
                        ]}
                      />
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.transformOperation !== curr.transformOperation}>
                      {() => {
                        const transformOp = form.getFieldValue('transformOperation');

                        if (transformOp === 'filter') {
                          return (
                            <Form.Item name="filterCondition" label={pa('filterCondition')} rules={[{ required: true }]}>
                              <TextArea rows={2} placeholder="item.salary > 1000" />
                            </Form.Item>
                          );
                        }

                        if (transformOp === 'map') {
                          return (
                            <Form.Item name="mapExpression" label={pa('mapExpression')} rules={[{ required: true }]}>
                              <TextArea rows={3} placeholder="{ name: item.fullName, code: item.abSoftCode }" />
                            </Form.Item>
                          );
                        }

                        if (transformOp === 'pluck') {
                          return (
                            <Form.Item name="pluckFields" label={pa('pluckFields')} rules={[{ required: true }]}>
                              <Input placeholder={pa('pluckFieldsPlaceholder')} />
                            </Form.Item>
                          );
                        }

                        if (transformOp === 'flatten') {
                          return (
                            <Form.Item name="flattenDepth" label={pa('flattenDepth')} initialValue={1}>
                              <InputNumber min={1} max={10} style={{ width: '100%' }} />
                            </Form.Item>
                          );
                        }

                        if (transformOp === 'sort') {
                          return (
                            <>
                              <Form.Item name="sortBy" label={pa('sortBy')}>
                                <Input placeholder={pa('sortByPlaceholder')} />
                              </Form.Item>
                              <Form.Item name="sortOrder" label={pa('sortOrder')} initialValue="asc">
                                <Select
                                  options={[
                                    { value: 'asc', label: pa('ascending') },
                                    { value: 'desc', label: pa('descending') },
                                  ]}
                                />
                              </Form.Item>
                            </>
                          );
                        }

                        if (transformOp === 'custom') {
                          return (
                            <Form.Item name="customExpression" label={pa('customExpression')} rules={[{ required: true }]}>
                              <TextArea
                                rows={4}
                                placeholder="inputData.filter(e => e.type === 'foreign')"
                              />
                            </Form.Item>
                          );
                        }

                        // For 'unique' and 'count', no additional fields needed
                        return null;
                      }}
                    </Form.Item>
                  </>
                );
              }

              return null;
            }}
          </Form.Item>

          <Form.Item name="outputVariable" label={pa('outputVariableName')}>
            <Input placeholder={pa('outputVariablePlaceholder')} />
          </Form.Item>

          <Form.Item name="condition" label={pa('conditionalExecution')}>
            <Input placeholder={pa('conditionalExecutionPlaceholder')} />
          </Form.Item>

          <Form.Item name="onError" label={pa('onError')} initialValue="stop">
            <Select
              options={[
                { value: 'stop', label: pa('stopPipeline') },
                { value: 'continue', label: pa('continueToNext') },
                { value: 'retry', label: pa('retryStep') },
              ]}
            />
          </Form.Item>

          <Form.Item name="maxRetries" label={pa('maxRetries')} initialValue={0}>
            <InputNumber min={0} max={5} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
